export enum OrganizationUserRole {
  ADMIN = "ADMIN",
  MEMBER = "MEMBER",
  SUPER_ADMIN = "SUPER_ADMIN",
}

export enum OrganizationUserRoleLabel {
  ADMIN = "Admin",
  MEMBER = "Member",
  SUPER_ADMIN = "Super Admin",
}

// WorkOS prefixes organization-level custom roles with `org-`. System roles
// (admin, member) are plain slugs. This map resolves both shapes to our enum.
export const WORKOS_SUPER_ADMIN_ROLE_SLUG = "org-super-admin";

const WORKOS_ROLE_SLUG_TO_ENUM: Record<string, OrganizationUserRole> = {
  admin: OrganizationUserRole.ADMIN,
  member: OrganizationUserRole.MEMBER,
  [WORKOS_SUPER_ADMIN_ROLE_SLUG]: OrganizationUserRole.SUPER_ADMIN,
};

export function mapWorkOSRoleSlugToOrganizationUserRole(
  roleSlug: string | null | undefined
): OrganizationUserRole | null {
  if (!roleSlug) return null;
  return WORKOS_ROLE_SLUG_TO_ENUM[roleSlug.toLowerCase()] ?? null;
}

// Roles an organization may assign to its own members. SUPER_ADMIN is a
// platform-level (Skrift staff) role granted out-of-band — it must never be
// offered by, or accepted from, org-facing user management. Both the role
// selectors and the admin user APIs derive their allow-list from this constant.
export const ORG_ASSIGNABLE_ROLES: readonly OrganizationUserRole[] = [
  OrganizationUserRole.ADMIN,
  OrganizationUserRole.MEMBER,
];

export function isOrgAssignableRole(
  role: OrganizationUserRole | string | null | undefined
): boolean {
  return ORG_ASSIGNABLE_ROLES.includes(role as OrganizationUserRole);
}

// Ready-made `{ value, label }` options for org-facing role selectors, so no
// caller has to remember to filter the full enum.
export const ORG_ASSIGNABLE_ROLE_OPTIONS: { value: string; label: string }[] =
  ORG_ASSIGNABLE_ROLES.map((role) => ({
    value: role,
    label: OrganizationUserRoleLabel[role],
  }));

// Roles that should see and use ADMIN-gated UI. SUPER_ADMIN is a strict superset
// of ADMIN, so it is included. UI-only: the real authorization boundary is the
// API guards and the route-protection middleware.
export function isAdminRole(
  role: OrganizationUserRole | null | undefined
): boolean {
  return (
    role === OrganizationUserRole.ADMIN ||
    role === OrganizationUserRole.SUPER_ADMIN
  );
}

// Slug-based companion for callers holding raw WorkOS role slugs instead of the
// enum (e.g. a session role-slug array).
export function isAdminRoleSlug(slug: string | null | undefined): boolean {
  return isAdminRole(mapWorkOSRoleSlugToOrganizationUserRole(slug));
}

export enum OrganizationUserStatus {
  ACTIVE = "ACTIVE",
  INVITED = "INVITED",
  PENDING_INVITATION = "PENDING_INVITATION",
  DEACTIVATED = "DEACTIVATED",
}

export enum OrganizationUserStatusLabel {
  ACTIVE = "Active",
  INVITED = "Invited",
  PENDING_INVITATION = "Pending Invitation",
  DEACTIVATED = "Deactivated",
}
